import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import styles from './MetricsDashboard.module.css';

/**
 * Adoption and usage dashboard.
 *
 * Reads the anonymous counter table filled by UsageTelemetryMiddleware. Nothing
 * here counts unique people — the API has no per-device data to give, on purpose
 * — so the page is careful to say "requests" and "launches" rather than "users",
 * and points at the store consoles for the numbers only they can answer.
 *
 * Charts are inline SVG rather than a charting library: the site has no chart
 * dependency today and one dashboard is not a good reason to add ~150KB.
 */

// Fixed slot order. A platform absent from the window must not repaint the
// others, so color is looked up by name and never by position in the response.
const PLATFORM_ORDER = ['web', 'mobile', 'android', 'ios', 'other'] as const;
type Platform = (typeof PLATFORM_ORDER)[number];

const PLATFORM_COLOR: Record<string, string> = {
  web: 'var(--mx-series-web)',
  mobile: 'var(--mx-series-mobile)',
  android: 'var(--mx-series-android)',
  ios: 'var(--mx-series-ios)',
  other: 'var(--mx-series-other)',
};

const PLATFORM_LABEL: Record<string, string> = {
  web: 'Website',
  mobile: 'Mobile (1.0.8-)',
  android: 'Android',
  ios: 'iOS',
  other: 'Other',
};

// Short forms for the end-of-line labels inside the plot. The full name would be
// clipped by the right gutter, and the legend right above the chart already
// carries the disambiguating version.
const PLATFORM_SHORT: Record<string, string> = {
  web: 'Website',
  mobile: 'Mobile',
  android: 'Android',
  ios: 'iOS',
  other: 'Other',
};

interface PlatformSlice { platform: string; requests: number; launches: number; }
interface SummaryHalf {
  requests: number;
  launches: number;
  errorRate: number;
  avgMs: number;
  byPlatform: PlatformSlice[];
}
interface Summary {
  windowDays: number;
  from: string;
  to: string;
  current: SummaryHalf;
  previous: SummaryHalf;
  caveats: string[];
}
interface DailyRow { date: string; platform: string; requests: number; launches: number; errors: number; }
interface RouteRow { route: string; method: string; requests: number; errors: number; avgMs: number; maxMs: number; }
interface VersionRow { platform: string; version: string; requests: number; launches: number; lastSeen: string; }
interface OrgMetrics {
  users: { total: number; online: number; activeLast30Days: number };
  content: { posts: number; events: number; eventRsvps: number; shipRatings: number; shipReviews: number };
  catalog: { ships: number; blueprints: number };
}
interface Health {
  healthy: boolean;
  lastTickUtc: string | null;
  minutesSinceTick: number | null;
  lastFlushUtc: string | null;
  minutesSinceFlush: number | null;
  storedRows: number;
  latestDataDate: string | null;
  lastFlushError: string | null;
  droppedForCardinality: number;
  note: string | null;
}

const nf = new Intl.NumberFormat();
const fmt = (n: number) => nf.format(Math.round(n));

/** Percentage change, or null when there is no baseline to compare against. */
function pctChange(now: number, before: number): number | null {
  if (!before) return null;
  return ((now - before) / before) * 100;
}

function shortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** Round a max up to something a human would pick for the top gridline. */
function niceMax(value: number): number {
  if (value <= 5) return 5;
  const mag = Math.pow(10, Math.floor(Math.log10(value)));
  const step = [1, 2, 2.5, 5, 10].find((s) => value <= s * mag) ?? 10;
  return step * mag;
}

// ── stat tile ───────────────────────────────────────────────────────────────

const StatTile: React.FC<{
  label: string;
  value: string;
  change?: number | null;
  /** For error rate and latency a rise is bad; for traffic a rise is good. */
  higherIsBetter?: boolean;
  meta?: string;
}> = ({ label, value, change, higherIsBetter = true, meta }) => {
  // Three distinct states, and they must not be conflated:
  //   change undefined -> this tile has no time window at all (org totals), so
  //                       showing a trend slot would be noise. Render meta only.
  //   change null      -> windowed, but the previous window has nothing to compare.
  //   change number    -> a real delta.
  const hasTrend = change !== undefined;

  let trendClass = styles.trendFlat;
  let trendText = 'no prior data';

  if (change !== null && change !== undefined) {
    const better = higherIsBetter ? change > 0 : change < 0;
    if (Math.abs(change) < 0.5) {
      trendClass = styles.trendFlat;
      trendText = 'flat';
    } else {
      trendClass = better ? styles.trendUp : styles.trendDown;
      trendText = `${change > 0 ? '+' : ''}${change.toFixed(1)}%`;
    }
  }

  return (
    <div className={styles.statTile}>
      <div className={styles.statLabel}>{label}</div>
      <div className={styles.statValue}>{value}</div>
      <div className={styles.statMeta}>
        {hasTrend && <span className={trendClass}>{trendText}</span>}
        {hasTrend && meta ? <> · </> : null}
        {meta}
      </div>
    </div>
  );
};

// ── line chart ──────────────────────────────────────────────────────────────

interface Series { platform: string; points: number[]; }

const LineChart: React.FC<{ dates: string[]; series: Series[]; metricLabel: string }> = ({
  dates,
  series,
  metricLabel,
}) => {
  const [hover, setHover] = useState<number | null>(null);

  const W = 900;
  const H = 300;
  const pad = { top: 16, right: 68, bottom: 30, left: 46 };
  const plotW = W - pad.left - pad.right;
  const plotH = H - pad.top - pad.bottom;

  const rawMax = Math.max(1, ...series.flatMap((s) => s.points));
  const yMax = niceMax(rawMax);

  const x = (i: number) => (dates.length <= 1 ? pad.left : pad.left + (i / (dates.length - 1)) * plotW);
  const y = (v: number) => pad.top + plotH - (v / yMax) * plotH;

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(yMax * f));
  const uniqueTicks = Array.from(new Set(ticks));

  // Label roughly six x positions so they never collide, always including the ends.
  const labelEvery = Math.max(1, Math.ceil(dates.length / 6));

  // Today is still accumulating, so its point is always lower than a finished day.
  // Left alone it draws a cliff at the right edge every single day and reads as a
  // crash. The final segment is dashed and the axis says so, rather than hiding it.
  const todayIso = new Date().toISOString().slice(0, 10);
  const lastIsPartial = dates.length > 1 && dates[dates.length - 1] === todayIso;

  /**
   * End-of-line labels, pushed apart so converging series stay readable.
   *
   * Without this the labels stack into an unreadable pile wherever the lines
   * meet — which is most of the time, since several platforms sit near zero.
   * Sort by y, enforce a minimum gap downward, then correct anything shoved past
   * the bottom of the plot back upward.
   */
  const MIN_LABEL_GAP = 13;
  const labelY = (() => {
    const items = series.map((s) => ({
      platform: s.platform,
      pos: y(s.points[s.points.length - 1] ?? 0),
    }));
    items.sort((a, b) => a.pos - b.pos);
    for (let i = 1; i < items.length; i++) {
      if (items[i].pos - items[i - 1].pos < MIN_LABEL_GAP) {
        items[i].pos = items[i - 1].pos + MIN_LABEL_GAP;
      }
    }
    const bottom = pad.top + plotH;
    for (let i = items.length - 1; i >= 0; i--) {
      const limit = bottom - (items.length - 1 - i) * MIN_LABEL_GAP;
      if (items[i].pos > limit) items[i].pos = limit;
    }
    return new Map(items.map((it) => [it.platform, it.pos] as const));
  })();

  const handleMove = useCallback(
    (e: React.MouseEvent<SVGRectElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const ratio = (e.clientX - rect.left) / rect.width;
      const idx = Math.round(ratio * (dates.length - 1));
      setHover(Math.max(0, Math.min(dates.length - 1, idx)));
    },
    [dates.length]
  );

  const tooltipLeftPct = hover === null ? 0 : (x(hover) / W) * 100;
  const flip = tooltipLeftPct > 60;

  return (
    <div className={styles.chartWrap}>
      <svg
        className={styles.chartSvg}
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`${metricLabel} per day by platform`}
        preserveAspectRatio="xMidYMid meet"
      >
        {uniqueTicks.map((t) => (
          <g key={t}>
            <line className={styles.gridLine} x1={pad.left} x2={pad.left + plotW} y1={y(t)} y2={y(t)} />
            <text className={styles.axisText} x={pad.left - 8} y={y(t) + 4} textAnchor="end">
              {fmt(t)}
            </text>
          </g>
        ))}

        {dates.map((d, i) =>
          i % labelEvery === 0 || i === dates.length - 1 ? (
            <text key={d} className={styles.axisText} x={x(i)} y={H - 10} textAnchor="middle">
              {shortDate(d)}
            </text>
          ) : null
        )}

        {hover !== null && (
          <line className={styles.crosshair} x1={x(hover)} x2={x(hover)} y1={pad.top} y2={pad.top + plotH} />
        )}

        {series.map((s) => {
          const color = PLATFORM_COLOR[s.platform] ?? PLATFORM_COLOR.other;
          const lastIdx = s.points.length - 1;

          // Solid through the last COMPLETE day; the partial tail is dashed.
          const solidUpto = lastIsPartial ? lastIdx - 1 : lastIdx;
          const solid = s.points
            .slice(0, solidUpto + 1)
            .map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(v)}`)
            .join(' ');
          const tail = lastIsPartial
            ? `M${x(solidUpto)},${y(s.points[solidUpto])} L${x(lastIdx)},${y(s.points[lastIdx])}`
            : null;

          const ly = labelY.get(s.platform) ?? y(s.points[lastIdx] ?? 0);

          return (
            <g key={s.platform}>
              <path d={solid} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
              {tail && (
                <path d={tail} fill="none" stroke={color} strokeWidth={2} strokeDasharray="4 3" strokeLinecap="round" />
              )}

              {/* Direct label at the line end, de-collided above. With <=4 series
                  this is what keeps identity off color alone, which the light-mode
                  contrast warning on two of these hues requires. A leader line is
                  drawn when the label had to move off its own point. */}
              {lastIdx >= 0 && (
                <>
                  {Math.abs(ly - y(s.points[lastIdx])) > 2 && (
                    <line
                      x1={x(lastIdx)}
                      y1={y(s.points[lastIdx])}
                      x2={x(lastIdx) + 6}
                      y2={ly - 4}
                      stroke={color}
                      strokeWidth={1}
                      opacity={0.6}
                    />
                  )}
                  <text className={styles.seriesLabel} x={x(lastIdx) + 8} y={ly} fill={color}>
                    {PLATFORM_SHORT[s.platform] ?? s.platform}
                  </text>
                </>
              )}

              {hover !== null && (
                /* 2px surface ring so overlapping markers stay separable. */
                <circle cx={x(hover)} cy={y(s.points[hover])} r={4.5} fill={color} stroke="var(--bg-card)" strokeWidth={2} />
              )}
            </g>
          );
        })}

        <rect
          x={pad.left}
          y={pad.top}
          width={plotW}
          height={plotH}
          fill="transparent"
          onMouseMove={handleMove}
          onMouseLeave={() => setHover(null)}
        />
      </svg>

      {lastIsPartial && (
        <div className={styles.chartCaption}>
          Dashed segment is today, still accumulating — expect it to sit below a finished day.
        </div>
      )}

      {hover !== null && (
        <div
          className={styles.tooltip}
          style={{
            left: `${tooltipLeftPct}%`,
            top: 8,
            transform: flip ? 'translateX(-105%)' : 'translateX(12px)',
          }}
        >
          <div className={styles.tooltipDate}>{shortDate(dates[hover])}</div>
          {series.map((s) => (
            <div key={s.platform} className={styles.tooltipRow}>
              <span className={styles.tooltipKey}>
                <span
                  className={styles.legendSwatch}
                  style={{ background: PLATFORM_COLOR[s.platform] ?? PLATFORM_COLOR.other }}
                />
                {PLATFORM_LABEL[s.platform] ?? s.platform}
              </span>
              <span className={styles.tooltipValue}>{fmt(s.points[hover])}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── page ────────────────────────────────────────────────────────────────────

const RANGES = [7, 30, 90];

// Client filter for the endpoint table. "Mobile" is the app family — the API folds
// mobile/android/ios together — so the answer stays stable when a build starts
// reporting its real platform. Default is Mobile: this table is read to learn which
// app features get used, and unfiltered it is dominated by the website's sidebar
// counters, which poll from every page including this one.
const ROUTE_PLATFORMS: { key: string; label: string }[] = [
  { key: 'mobile', label: 'Mobile app' },
  { key: 'web', label: 'Website' },
  { key: 'all', label: 'Everything' },
];

const MetricsDashboard: React.FC = () => {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [routePlatform, setRoutePlatform] = useState<string>('mobile');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTable, setShowTable] = useState(false);

  const [summary, setSummary] = useState<Summary | null>(null);
  const [daily, setDaily] = useState<DailyRow[]>([]);
  const [routes, setRoutes] = useState<RouteRow[]>([]);
  const [versions, setVersions] = useState<VersionRow[]>([]);
  const [org, setOrg] = useState<OrgMetrics | null>(null);
  const [health, setHealth] = useState<Health | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [s, d, r, v, o, h] = await Promise.all([
          api.get<Summary>(`/metrics/usage/summary?days=${days}`),
          api.get<DailyRow[]>(`/metrics/usage/daily?days=${days}`),
          api.get<RouteRow[]>(`/metrics/usage/routes?days=${days}&limit=20&platform=${routePlatform}`),
          api.get<VersionRow[]>(`/metrics/usage/versions?days=${days}`),
          api.get<OrgMetrics>('/metrics/org'),
          api.get<Health>('/metrics/health'),
        ]);
        if (cancelled) return;
        setSummary(s.data);
        setDaily(d.data);
        setRoutes(r.data);
        setVersions(v.data);
        setOrg(o.data);
        setHealth(h.data);
      } catch (err: any) {
        if (!cancelled) setError(err?.response?.data?.message ?? err?.message ?? 'Failed to load metrics');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [days, routePlatform]);

  /** Pivot the flat daily rows into one dense series per platform. */
  const { dates, series } = useMemo(() => {
    const dateSet = Array.from(new Set(daily.map((r) => r.date))).sort();
    const present = PLATFORM_ORDER.filter((p) => daily.some((r) => r.platform === p));
    const byKey = new Map(daily.map((r) => [`${r.date}|${r.platform}`, r.launches]));

    return {
      dates: dateSet,
      series: present.map<Series>((p) => ({
        platform: p,
        // A day with no traffic is a real zero, not a gap.
        points: dateSet.map((d) => byKey.get(`${d}|${p}`) ?? 0),
      })),
    };
  }, [daily]);

  const platformSplit = useMemo(() => {
    if (!summary) return [];
    const total = summary.current.byPlatform.reduce((a, b) => a + b.requests, 0);
    return PLATFORM_ORDER.map((p) => {
      const row = summary.current.byPlatform.find((b) => b.platform === p);
      return { platform: p as Platform, requests: row?.requests ?? 0, launches: row?.launches ?? 0, total };
    }).filter((r) => r.requests > 0);
  }, [summary]);

  const maxRouteRequests = routes.length ? Math.max(...routes.map((r) => r.requests)) : 1;

  if (!user) return <Navigate to="/login" replace />;

  // Owner-only, by NAME rather than by role. Role "Admin,Leader" would have opened
  // this to nine Leader accounts. This is only the cosmetic gate — the real one is
  // the MetricsViewer policy on the API, which returns 403 regardless of what the
  // browser decides to render.
  const allowed = (process.env.REACT_APP_METRICS_USERS ?? 'DigitalMaster')
    .split(',')
    .map((n) => n.trim().toLowerCase())
    .filter(Boolean);
  const username = String(user.unique_name ?? user.username ?? user.name ?? '').toLowerCase();
  if (!allowed.includes(username)) return <Navigate to="/home" replace />;

  const hasUsageData = summary !== null && summary.current.requests > 0;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>App Health &amp; Adoption</h1>
          <p className={styles.subtitle}>
            Usage measured from our own API traffic. These are requests and app launches, not people —
            installs, uninstalls and retention live in the Play Console and App Store Connect.
          </p>
        </div>
        <div className={styles.filterRow}>
          {RANGES.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setDays(r)}
              className={`${styles.rangeButton} ${days === r ? styles.rangeButtonActive : ''}`}
            >
              {r}d
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className={`${styles.banner} ${styles.bannerWarn}`}>
          <div className={styles.bannerTitle}>Could not load metrics</div>
          {error}
        </div>
      )}

      {/* The pipeline can fail silently — a dashboard of zeroes looks exactly like
          a quiet week. Say which one it is. */}
      {health && !health.healthy && (
        <div className={`${styles.banner} ${styles.bannerWarn}`}>
          <div className={styles.bannerTitle}>Telemetry pipeline is not reporting</div>
          {health.lastFlushError
            ? `Last write failed: ${health.lastFlushError}`
            : health.lastTickUtc
            ? `The collector loop last ran ${Math.round(health.minutesSinceTick ?? 0)} minutes ago; it should run every minute. Quiet traffic is not the cause — an idle loop still ticks.`
            : 'The background flusher has not run at all since this API instance started.'}
          {health.droppedForCardinality > 0 && (
            <div style={{ marginTop: '0.4rem' }}>
              {fmt(health.droppedForCardinality)} request(s) were dropped at the bucket ceiling — a route is
              probably being recorded with a literal value in it.
            </div>
          )}
        </div>
      )}

      {!loading && !hasUsageData && (
        <div className={`${styles.banner} ${styles.bannerInfo}`}>
          <div className={styles.bannerTitle}>No usage data for this window yet</div>
          Telemetry only counts traffic from the moment it is deployed — it cannot reconstruct the past.
          {health?.latestDataDate
            ? ` Collection has data through ${health.latestDataDate}; try a wider window.`
            : ' Once this ships to production, numbers appear within a minute of the first request.'}
        </div>
      )}

      {loading ? (
        <div className={styles.card}>
          <div className={styles.emptyState}>Loading metrics…</div>
        </div>
      ) : (
        <>
          {summary && (
            <div className={styles.statGrid}>
              <StatTile
                label="App launches"
                value={fmt(summary.current.launches)}
                change={pctChange(summary.current.launches, summary.previous.launches)}
                meta={`last ${days}d`}
              />
              <StatTile
                label="API requests"
                value={fmt(summary.current.requests)}
                change={pctChange(summary.current.requests, summary.previous.requests)}
                meta={`last ${days}d`}
              />
              <StatTile
                label="Error rate"
                value={`${summary.current.errorRate.toFixed(2)}%`}
                change={pctChange(summary.current.errorRate, summary.previous.errorRate)}
                higherIsBetter={false}
                meta="4xx + 5xx"
              />
              <StatTile
                label="Avg response"
                value={`${fmt(summary.current.avgMs)} ms`}
                change={pctChange(summary.current.avgMs, summary.previous.avgMs)}
                higherIsBetter={false}
                meta="all endpoints"
              />
            </div>
          )}

          {dates.length > 0 && series.length > 0 && (
            <div className={styles.card}>
              <h2 className={styles.cardTitle}>App launches per day</h2>
              <p className={styles.cardNote}>
                The app calls the version endpoint once on launch, so this counts launches without
                identifying anyone. It cannot tell a returning user from a new one.
              </p>

              <div className={styles.legend}>
                {series.map((s) => (
                  <span key={s.platform} className={styles.legendItem}>
                    <span
                      className={styles.legendSwatch}
                      style={{ background: PLATFORM_COLOR[s.platform] ?? PLATFORM_COLOR.other }}
                    />
                    {PLATFORM_LABEL[s.platform] ?? s.platform}
                  </span>
                ))}
              </div>

              <LineChart dates={dates} series={series} metricLabel="App launches" />

              <button
                type="button"
                className={styles.rangeButton}
                style={{ marginTop: '0.75rem' }}
                onClick={() => setShowTable((v) => !v)}
              >
                {showTable ? 'Hide data table' : 'Show data table'}
              </button>

              {showTable && (
                <div className={styles.tableWrap} style={{ marginTop: '0.75rem' }}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        {series.map((s) => (
                          <th key={s.platform} style={{ textAlign: 'right' }}>
                            {PLATFORM_LABEL[s.platform] ?? s.platform}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {dates.map((d, i) => (
                        <tr key={d}>
                          <td>{shortDate(d)}</td>
                          {series.map((s) => (
                            <td key={s.platform} className={styles.num}>
                              {fmt(s.points[i])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {platformSplit.length > 0 && (
            <div className={styles.card}>
              <h2 className={styles.cardTitle}>Where the traffic comes from</h2>
              <p className={styles.cardNote}>
                Share of API requests over the last {days} days.
                {platformSplit.some((p) => p.platform === 'mobile') && (
                  <>
                    {' '}
                    &quot;Mobile (1.0.8-)&quot; is app builds that send a fixed User-Agent naming no platform,
                    so their iOS and Android traffic cannot be separated. That resolves once a build ships
                    that reports its platform.
                  </>
                )}
              </p>
              {platformSplit.map((p) => (
                <div key={p.platform} className={styles.barRow}>
                  <span className={styles.barLabel}>{PLATFORM_LABEL[p.platform]}</span>
                  <div className={styles.barTrack}>
                    <div
                      className={styles.barFill}
                      style={{
                        width: `${p.total ? (p.requests / p.total) * 100 : 0}%`,
                        background: PLATFORM_COLOR[p.platform],
                      }}
                    />
                  </div>
                  <span className={styles.barValue}>
                    {p.total ? ((p.requests / p.total) * 100).toFixed(1) : '0.0'}%
                  </span>
                </div>
              ))}
            </div>
          )}

          {!loading && (
            <div className={styles.card}>
              <h2 className={styles.cardTitle}>Most-used endpoints</h2>
              <p className={styles.cardNote}>
                The closest thing we have to feature popularity: which parts of the app people actually reach.
                Paths are route templates, so no record exists of what any individual looked at.
              </p>
              <p className={styles.cardNote}>
                Split by client, because the website and the app do not ask the same questions. The site&apos;s
                sidebar polls its badge counters from every page &mdash; including this one &mdash; so
                &quot;Everything&quot; mostly reflects who has a tab open, not which features get used.
              </p>
              <div className={styles.filterRow}>
                {ROUTE_PLATFORMS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setRoutePlatform(p.key)}
                    className={`${styles.rangeButton} ${routePlatform === p.key ? styles.rangeButtonActive : ''}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              {routes.length === 0 ? (
                <p className={styles.cardNote}>Nothing recorded from this client in the selected range.</p>
              ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Endpoint</th>
                      <th style={{ width: '22%' }}>Share</th>
                      <th style={{ textAlign: 'right' }}>Requests</th>
                      <th style={{ textAlign: 'right' }}>Errors</th>
                      <th style={{ textAlign: 'right' }}>Avg</th>
                      <th style={{ textAlign: 'right' }}>Max</th>
                    </tr>
                  </thead>
                  <tbody>
                    {routes.map((r) => (
                      <tr key={`${r.method}|${r.route}`}>
                        <td>
                          <span className={styles.methodTag}>{r.method}</span>{' '}
                          <span className={styles.routeCell}>{r.route}</span>
                        </td>
                        <td>
                          <div className={styles.miniBarTrack}>
                            <div
                              className={styles.miniBarFill}
                              style={{ width: `${(r.requests / maxRouteRequests) * 100}%` }}
                            />
                          </div>
                        </td>
                        <td className={styles.num}>{fmt(r.requests)}</td>
                        <td className={styles.num}>{fmt(r.errors)}</td>
                        <td className={styles.num}>{fmt(r.avgMs)} ms</td>
                        <td className={styles.num}>{fmt(r.maxMs)} ms</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          )}

          {versions.length > 0 && (
            <div className={styles.card}>
              <h2 className={styles.cardTitle}>App version adoption</h2>
              <p className={styles.cardNote}>
                Measured from our own traffic rather than the store consoles. Only meaningful once a build
                ships that reports a real version — everything up to 1.0.8 hardcodes &quot;1.0&quot;.
              </p>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Platform</th>
                      <th>Version</th>
                      <th style={{ textAlign: 'right' }}>Launches</th>
                      <th style={{ textAlign: 'right' }}>Requests</th>
                      <th style={{ textAlign: 'right' }}>Last seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {versions.map((v) => (
                      <tr key={`${v.platform}|${v.version}`}>
                        <td>
                          <span
                            className={styles.legendSwatch}
                            style={{
                              background: PLATFORM_COLOR[v.platform] ?? PLATFORM_COLOR.other,
                              display: 'inline-block',
                              marginRight: '0.4rem',
                            }}
                          />
                          {PLATFORM_LABEL[v.platform] ?? v.platform}
                        </td>
                        <td>{v.version}</td>
                        <td className={styles.num}>{fmt(v.launches)}</td>
                        <td className={styles.num}>{fmt(v.requests)}</td>
                        <td className={styles.num}>{v.lastSeen}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {org && (
            <div className={styles.card}>
              <h2 className={styles.cardTitle}>Organisation</h2>
              <p className={styles.cardNote}>
                Members and content on tficorg.org. Counted in the database rather than by downloading
                every record.
              </p>
              <div className={styles.statGrid} style={{ marginBottom: 0 }}>
                <StatTile label="Members" value={fmt(org.users.total)} meta={`${fmt(org.users.online)} online now`} />
                <StatTile label="Active (30d)" value={fmt(org.users.activeLast30Days)} meta="signed in" />
                <StatTile label="Events" value={fmt(org.content.events)} meta={`${fmt(org.content.eventRsvps)} RSVPs`} />
                <StatTile
                  label="Ship feedback"
                  value={fmt(org.content.shipRatings + org.content.shipReviews)}
                  meta={`${fmt(org.content.shipRatings)} ratings · ${fmt(org.content.shipReviews)} reviews`}
                />
                <StatTile label="Ships in catalog" value={fmt(org.catalog.ships)} meta="from the game files" />
                <StatTile label="Blueprints" value={fmt(org.catalog.blueprints)} meta="from the game files" />
              </div>
            </div>
          )}

          <div className={styles.card}>
            <h2 className={styles.cardTitle}>What this page cannot tell you</h2>
            <ul className={styles.caveats}>
              {(summary?.caveats ?? []).map((c) => (
                <li key={c}>{c}</li>
              ))}
              <li>
                Installs, uninstalls, retention and crash rates are only available in the Play Console and
                App Store Connect. Nothing here substitutes for them.
              </li>
              <li>
                Counts start when telemetry was deployed. There is no history before that, by construction.
              </li>
              {health && (
                <li>
                  Pipeline: {health.healthy ? 'healthy' : 'NOT REPORTING'} · {fmt(health.storedRows)} stored rows ·
                  latest data {health.latestDataDate ?? 'none'}.
                </li>
              )}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default MetricsDashboard;
